import { useEffect, useRef, useState, memo } from 'react';

interface UseScrollRevealOptions {
  threshold?: number;
  delay?: number;
  once?: boolean;
}

export const useScrollReveal = (options: UseScrollRevealOptions = {}) => {
  const { threshold = 0.15, delay = 0, once = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (delay > 0) {
            setTimeout(() => setIsVisible(true), delay);
          } else {
            setIsVisible(true);
          }
          if (once) observer.disconnect();
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, delay, once]);

  return { ref, isVisible };
};

interface ScrollRevealProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  threshold?: number;
}

// Optimized ScrollReveal with GPU acceleration - uses CSS classes to prevent forced reflow
export const ScrollReveal = memo(({ 
  children, 
  delay = 0, 
  className = '',
  threshold = 0.15
}: ScrollRevealProps) => {
  const { ref, isVisible } = useScrollReveal({ delay, threshold });

  return (
    <div
      ref={ref}
      className={`${className} scroll-reveal-container ${isVisible ? 'scroll-reveal-visible' : 'scroll-reveal-hidden'}`}
      style={{
        // Only delay is dynamic, rest is handled by CSS classes to avoid forced reflow
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
});
